import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const slides = [
  {
    image: '/Images/2026/gadaa-2026-7.jpg',
    alt: 'Community members in traditional dress at the Annual Gadaa Meeting 2026',
    eyebrow: 'Oromo Gadaa-UK',
    title: 'Welcome to Oromo Gadaa-UK',
    description: 'Preserving our heritage, building our future together'
  },
  {
    image: '/Images/2026/gadaa-2026-4.jpg',
    alt: 'Community gathered on stage at the Annual Gadaa Meeting 2026',
    eyebrow: 'Tokkummaa',
    title: 'Unity in Diversity',
    description: 'Connecting Oromo communities across the United Kingdom'
  },
  {
    image: '/Images/2026/gadaa-2026-2.jpg',
    alt: 'Elders and families at the Annual Gadaa Meeting 2026',
    eyebrow: 'Aadaa',
    title: 'Cultural Heritage',
    description: 'Celebrating and preserving our rich Gadaa traditions'
  }
];

const values = [
  { word: 'Safuu', meaning: 'Ethics' },
  { word: 'Nagaa', meaning: 'Peace' },
  { word: 'Walabummaa', meaning: 'Freedom' },
  { word: 'Tokkummaa', meaning: 'Unity' }
];

const features = [
  { title: 'Cultural Preservation', text: 'We work tirelessly to preserve and pass on our Gadaa traditions, language, and cultural practices to future generations.' },
  { title: 'Community Support', text: 'Providing support and assistance to Oromo families and individuals settling and thriving in the UK.' },
  { title: 'Educational Programs', text: 'Offering language classes, cultural workshops, and educational programs for all age groups.' },
  { title: 'Social Events', text: 'Organizing festivals, celebrations, and social gatherings to strengthen community bonds.' }
];

const news = [
  { title: 'Annual Gadaa Festival 2025', text: 'Join us for our biggest celebration of the year featuring traditional music, dance, and food.', date: 'March 15, 2025' },
  { title: 'Oromo Language Classes', text: 'New semester of Afaan Oromoo classes starting for children and adults. Register now!', date: 'February 28, 2025' },
  { title: 'Community Meeting', text: 'Monthly community meeting to discuss upcoming events and community matters.', date: 'February 20, 2025' }
];

const pad = (n) => String(n).padStart(2, '0');

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [timerKey, setTimerKey] = useState(0);

  // Restart the auto-advance timer whenever the user navigates manually
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [timerKey]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide((index + slides.length) % slides.length);
    setTimerKey((k) => k + 1);
  }, []);

  return (
    <div className="home">
      <section className="hero">
        <div className="slider-container">
          <div className="slider">
            {slides.map((slide, index) => (
              <div key={slide.image} className={`slide ${index === currentSlide ? 'active' : ''}`}>
                <img src={slide.image} alt={slide.alt} />
                <div className="slide-content container">
                  <span className="eyebrow">{slide.eyebrow}</span>
                  <h2>{slide.title}</h2>
                  <p>{slide.description}</p>
                  <div className="hero-actions">
                    <Link className="btn btn-gold" to="/events">Upcoming Events</Link>
                    <Link className="btn btn-outline" to="/about">Our Story</Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="slider-controls container">
            <div className="slider-dots">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={index === currentSlide ? 'true' : undefined}
                ></button>
              ))}
            </div>
            <div className="slider-counter">
              <span className="current">{pad(currentSlide + 1)}</span> <span className="sep">/</span> {pad(slides.length)}
            </div>
            <div className="slider-nav">
              <button className="prev" onClick={() => goToSlide(currentSlide - 1)} aria-label="Previous slide">&#8592;</button>
              <button className="next" onClick={() => goToSlide(currentSlide + 1)} aria-label="Next slide">&#8594;</button>
            </div>
          </div>
        </div>
      </section>

      <section className="values-strip">
        <div className="container">
          <div className="values-row">
            {values.map(v => (
              <div key={v.word} className="value-pill"><strong>{v.word}</strong><span>{v.meaning}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section className="section welcome-section">
        <div className="container welcome-grid">
          <div className="welcome-text">
            <span className="eyebrow">Who we are</span>
            <h2>Welcome to Our Community</h2>
            <p>The Oromo Gadaa-UK is a vibrant community organization dedicated to preserving and promoting the rich cultural heritage of the Oromo people living in the United Kingdom. Our community serves as a bridge between our ancestral traditions and modern life in the UK.</p>
            <Link className="btn btn-dark" to="/about">Learn about Gadaa &rarr;</Link>
          </div>
          <div className="welcome-image">
            <img src="/Images/2025/together.jpeg" alt="Oromo community gathering" />
            <div className="welcome-badge">
              <strong>Gadaa</strong>
              <span>A living democratic tradition</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section features">
        <div className="container">
          <div className="section-head center">
            <span className="eyebrow">What we do</span>
            <h2>Rooted in tradition, serving today</h2>
          </div>
          <div className="feature-grid">
            {features.map((f, i) => (
              <div key={f.title} className="feature-card card">
                <span className="feature-num">{pad(i + 1)}</span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section news-section">
        <div className="container">
          <div className="news-head">
            <div>
              <span className="eyebrow">Latest</span>
              <h2>News &amp; Updates</h2>
            </div>
            <Link className="news-all" to="/events">See all events &rarr;</Link>
          </div>
          <div className="news-grid">
            {news.map(n => (
              <article key={n.title} className="news-item card">
                <span className="date">{n.date}</span>
                <h3>{n.title}</h3>
                <p>{n.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section home-cta">
        <div className="container">
          <div className="cta-band">
            <div>
              <h2>Be part of the community</h2>
              <p>Join a class, volunteer at an event, or simply come and say hello. Everyone is welcome.</p>
            </div>
            <div className="cta-details">
              <Link className="btn btn-gold" to="/contact">Get in touch</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
